import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { UserNotificationChannel } from '../../../../models/userNotificationChannel';
import { USER_NOTIFICATION_CHANNEL_STATUSES, USER_NOTIFICATION_CHANNEL_TYPES } from '../../../../consts/notifications';
import {
  getNotificationsChannels,
  deleteNotificationChannel,
  createNotificationChannel
} from 'Src/helpers/service/admin/user';
import { Switch } from '../../../../components/Switch/Switch';
import { Loader } from '../../../../components/Loader/Loader';
import { EventCommunicationPreferences } from 'Src/models/eventCommunication';
import { getEventCommunicationPreferences, updateEventCommunicationPreferences } from 'Src/helpers/service/eventCommunications';
import './NotificaionsTab.scss';

interface Props {
  user: AppUser;
}

interface State {
  isLoading: boolean;
  emailNotificationChannel: UserNotificationChannel;
  isEmailSwitchActive: boolean;
  eventCommunicationPreferences?: EventCommunicationPreferences;
  isSavingEventCommunications: boolean;
}

export class NotificaionsTab extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      emailNotificationChannel: undefined,
      isEmailSwitchActive: undefined,
      eventCommunicationPreferences: undefined,
      isSavingEventCommunications: false
    };
  }

  componentDidMount() {
    const { user } = this.props;

    this.setState({ isLoading: true });

    Promise.all([getNotificationsChannels(user), getEventCommunicationPreferences(user)]).then(([notificationChannels, eventCommunicationPreferences]) => {
      const emailNotificationChannel = notificationChannels.find(
        notificationChannel => notificationChannel.type === USER_NOTIFICATION_CHANNEL_TYPES.DEFAULT_EMAIL
      );
      const emailNotificationChannelStatus = propz.get(emailNotificationChannel, ['status']);
      const isEmailSwitchActive = emailNotificationChannelStatus === USER_NOTIFICATION_CHANNEL_STATUSES.ACTIVE;

      this.setState({ isEmailSwitchActive, emailNotificationChannel, eventCommunicationPreferences, isLoading: false });
    });
  }

  saveEventCommunicationPreference = async (key: keyof EventCommunicationPreferences, value: any) => {
    const current = this.state.eventCommunicationPreferences;
    if (!current) return;
    const next = {...current, [key]: value};
    this.setState({eventCommunicationPreferences: next, isSavingEventCommunications: true});
    try {
      const saved = await updateEventCommunicationPreferences(this.props.user, next);
      this.setState({eventCommunicationPreferences: saved, isSavingEventCommunications: false});
    } catch (error) {
      this.setState({eventCommunicationPreferences: current, isSavingEventCommunications: false});
    }
  };

  onSubmit = () => {
    const { user } = this.props;
    const { isEmailSwitchActive, emailNotificationChannel } = this.state;
    const emailNotificationChannelStatus = propz.get(emailNotificationChannel, ['status']);
    const isEmailNotificationChannelActive =
      emailNotificationChannelStatus === USER_NOTIFICATION_CHANNEL_STATUSES.ACTIVE;

    if (isEmailNotificationChannelActive !== isEmailSwitchActive) {
      this.setState({ isLoading: true });

      if (isEmailSwitchActive) {
        const data = { token: 'email', type: USER_NOTIFICATION_CHANNEL_TYPES.DEFAULT_EMAIL };
        createNotificationChannel(user, data).then(emailNotificationChannel => {
          this.setState({ emailNotificationChannel, isLoading: false });
        });
      } else {
        deleteNotificationChannel(user, emailNotificationChannel.id).then(() => {
          this.setState({ emailNotificationChannel: undefined, isLoading: false });
        });
      }
    }
  };

  onEmailSwitchChange = () => {
    this.setState(prevState => ({
      isEmailSwitchActive: !prevState.isEmailSwitchActive
    }));
  };

  render() {
    const { isEmailSwitchActive, isLoading, eventCommunicationPreferences, isSavingEventCommunications } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="upNotificationsTab">
        <section className="upProfileCard">
          <header className="upProfileCardHeader">
            <div>
              <h2 className="upProfileCardTitle">Notifications</h2>
              <p className="upProfileCardSub">Choose how Squad In Touch keeps you up to date.</p>
            </div>
          </header>
          <div className="upProfileCardBody">
            <div className="upProfileInfo">
              <span className="upProfileInfoIcon">i</span>
              <div>
                <div>Email notifications can be managed here.</div>
                <div>Text notifications depend on your school setup. Push preferences are managed in the mobile app.</div>
              </div>
            </div>

            <div className="upNotificationRow">
              <div>
                <div className="upNotificationTitle">Email notifications</div>
                <div className="upNotificationHint">Receive account and school communication updates by email.</div>
              </div>
              <Switch
                value={isEmailSwitchActive}
                text={''}
                name={'notifications_tab_switch'}
                onChange={this.onEmailSwitchChange}
              />
            </div>

            {eventCommunicationPreferences && <section className="upEventCommunicationPreferences">
              <header><h3>Chats</h3><p>These preferences apply to chats across your schools. You can still mute one conversation or event without changing them.</p></header>
              {([['privateEmail', 'Private chats and invitations'], ['mentionEmail', 'Mentions and replies']] as const).map(([key, label]) => <label key={key}><span><b>{label}</b><small>{key === 'privateEmail' ? 'When someone messages you or adds you to a private chat.' : 'When someone mentions you or replies to your message.'}</small></span><select disabled={isSavingEventCommunications} value={eventCommunicationPreferences[key]} onChange={event => this.saveEventCommunicationPreference(key, event.target.value)}><option value="IMMEDIATE">Email straight away</option><option value="DAILY_DIGEST">Daily summary at 08:00</option><option value="OFF">Off</option></select></label>)}
              {['ADMIN','MANAGER','TEACHER','COACH'].includes(this.props.user.activeRole) && <label><span><b>Messages to approve</b><small>Moderation alerts cannot be turned off completely.</small></span><select disabled={isSavingEventCommunications} value={eventCommunicationPreferences.moderationEmail} onChange={event => this.saveEventCommunicationPreference('moderationEmail', event.target.value)}><option value="IMMEDIATE">Email straight away</option><option value="DAILY_DIGEST">Daily summary at 08:00</option></select></label>}
              <label><span><b>Push notifications</b><small>Receive chat alerts in the mobile app.</small></span><Switch value={eventCommunicationPreferences.pushEnabled} text="" name="event_communications_push" onChange={() => this.saveEventCommunicationPreference('pushEnabled', !eventCommunicationPreferences.pushEnabled)} /></label>
            </section>}

            <button className="upProfileBtn upProfileBtnPrimary upNotificationsSave" onClick={this.onSubmit}>
              Save
            </button>
          </div>
        </section>
      </div>
    );
  }
}
